import { useState, type ReactNode } from 'react';
import { Box, HStack, Input, Text, VStack } from '@chakra-ui/react';
import Drawer, { DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter } from '@/components/ui/Drawer';
import Select from '@/components/ui/Select';
import Button from '@/components/ui/Button';
import { apiTime, localTime, errorMessage, memberName, useUpdateFrontlineMutation, type Contact, type FrontlineSetup, type PhoneMapping, type Shift } from '@/entities/frontline/api';
export type Editor = {
  kind: 'phone';
  value?: PhoneMapping;
} | {
  kind: 'contact';
  value?: Contact;
} | {
  kind: 'shift';
  value?: Shift;
};
function Field({
  title,
  children
}: {
  title: string;
  children: ReactNode;
}) {
  return <Box as="label" display="block" w="full"><Text fontSize="sm" mb={2}>{title}</Text>{children}</Box>;
}
export default function SetupEditor({
  setup,
  editor,
  onClose
}: {
  setup: FrontlineSetup;
  editor: Editor;
  onClose: () => void;
}) {
  const phone = editor.kind === 'phone' ? editor.value : undefined;
  const contact = editor.kind === 'contact' ? editor.value : undefined;
  const shift = editor.kind === 'shift' ? editor.value : undefined;
  const [phoneNumber, setPhoneNumber] = useState(phone?.phoneNumber ?? contact?.phoneNumber ?? '');
  const [agentId, setAgentId] = useState(phone?.retellAgentId ?? '');
  const [label, setLabel] = useState(phone?.label ?? '');
  const [active, setActive] = useState(phone?.isActive ?? contact?.acceptsEscalations ?? true);
  const [userId, setUserId] = useState(contact?.userId ?? '');
  const [primary, setPrimary] = useState(shift?.primaryUserId ?? '');
  const [backup, setBackup] = useState(shift?.backupUserId ?? '');
  const [startsAt, setStartsAt] = useState(shift ? localTime(shift.startsAt) : '');
  const [endsAt, setEndsAt] = useState(shift ? localTime(shift.endsAt) : '');
  const [error, setError] = useState('');
  const [save, {
    isLoading
  }] = useUpdateFrontlineMutation();
  const members = setup.members.map(m => ({
    value: m.id,
    label: memberName(m)
  }));
  const accepting = members.filter(m => setup.contacts.some(c => c.userId === m.value && c.acceptsEscalations));
  const submit = async () => {
    setError('');
    try {
      if (editor.kind === 'phone') {
        if (!phoneNumber.trim()) {
          setError('Enter the practice phone number.');
          return;
        }
        await save({
          path: phone ? `phone-numbers/${phone.id}` : 'phone-numbers',
          method: phone ? 'PATCH' : 'POST',
          body: {
            organizationId: setup.id,
            phoneNumber,
            retellAgentId: agentId.trim() || null,
            label: label.trim() || null,
            isActive: active
          }
        }).unwrap();
      } else if (editor.kind === 'contact') {
        if (!userId || !phoneNumber.trim()) {
          setError('Choose a member and enter their escalation phone number.');
          return;
        }
        await save({
          path: `organizations/${setup.id}/contacts/${userId}`,
          method: 'PUT',
          body: {
            phoneNumber,
            acceptsEscalations: active
          }
        }).unwrap();
      } else {
        if (!primary || !startsAt || !endsAt) {
          setError('Choose a primary doctor and the start and end times.');
          return;
        }
        if (localTime(apiTime(startsAt)) !== startsAt || localTime(apiTime(endsAt)) !== endsAt) {
          setError('This local time does not exist due to daylight saving. Choose another time.');
          return;
        }
        await save({
          path: `organizations/${setup.id}/shifts${shift ? `/${shift.id}` : ''}`,
          method: shift ? 'PATCH' : 'POST',
          body: {
            primaryUserId: primary,
            backupUserId: backup || null,
            startsAt: apiTime(startsAt),
            endsAt: apiTime(endsAt)
          }
        }).unwrap();
      }
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  const title = editor.kind === 'phone' ? 'Phone mapping' : editor.kind === 'contact' ? 'Escalation contact' : 'On-call shift';
  return <Drawer isOpen onClose={() => {
    if (!isLoading) onClose();
  }}><DrawerHeader><DrawerTitle>{editor.value ? 'Edit' : 'Add'} {title.toLowerCase()}</DrawerTitle></DrawerHeader><DrawerBody><VStack align="stretch" gap={5}>
    {error && <Text role="alert" color="red.600">{error}</Text>}
    {editor.kind === 'contact' && <Select label="Organization member" value={userId} onChange={setUserId} options={members} disabled={!!contact || isLoading} />}
    {editor.kind !== 'shift' && <Field title={editor.kind === 'phone' ? 'Practice number' : 'Doctor phone number'}><Input aria-label={editor.kind === 'phone' ? 'Practice number' : 'Doctor phone number'} type="tel" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} placeholder="+17815550100" /></Field>}
    {editor.kind === 'phone' && <><Field title="Retell agent ID"><Input aria-label="Retell agent ID" value={agentId} onChange={e => setAgentId(e.target.value)} placeholder="agent_…" /></Field><Text fontSize="sm" color="gray.500">The called number determines the organization. Agent ID checks that the expected agent handled it.</Text><Field title="Label"><Input aria-label="Label" value={label} maxLength={120} onChange={e => setLabel(e.target.value)} placeholder="Main practice line" /></Field></>}
    {editor.kind !== 'shift' && <Select label={editor.kind === 'phone' ? 'Mapping status' : 'Accept escalation notifications'} value={active ? 'yes' : 'no'} onChange={v => setActive(v === 'yes')} options={[{
          value: 'yes',
          label: 'Enabled'
        }, {
          value: 'no',
          label: 'Disabled'
        }]} />}
    {editor.kind === 'shift' && <><Text fontSize="sm">Enter times in your browser timezone: <strong>{Intl.DateTimeFormat().resolvedOptions().timeZone}</strong>. The schedule is displayed in {setup.timezone}.</Text><Select label="Primary doctor" options={accepting} value={primary} onChange={setPrimary} /><Select label="Backup doctor" options={[{
            value: '',
            label: 'No backup'
          }, ...accepting.filter(m => m.value !== primary)]} value={backup} onChange={setBackup} /><Field title="Starts at"><Input aria-label="Starts at" type="datetime-local" value={startsAt} onChange={e => setStartsAt(e.target.value)} /></Field><Field title="Ends at"><Input aria-label="Ends at" type="datetime-local" value={endsAt} onChange={e => setEndsAt(e.target.value)} /></Field><Text fontSize="sm" color="gray.500">Shifts may last up to seven days and cannot overlap. Only accepting contacts can be selected.</Text></>}
  </VStack></DrawerBody><DrawerFooter><HStack><Button variant="outline" disabled={isLoading} onClick={onClose}>Cancel</Button><Button loading={isLoading} onClick={() => void submit()}>Save</Button></HStack></DrawerFooter></Drawer>;
}
